import React, { FC, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppContextProvider } from '@components/agile/context/AppContext';
import KbarWrapper from '@components/devTool/kbar/KbarWrapper';
import { store } from '@store/store';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import logger from '@utils/logger';
import Loader from '@amzn/meridian/loader';
import Box from '@amzn/meridian/box';
import { TICKETING_PATH } from '@constants/routes';
import NotificationBanner from 'src/components/layout/NotificationBanner';
import ErrorBoundary from 'src/components/layout/ErrorBoundary';
import getMidwayRedirectRoutes from 'src/utils/midwayRedirectRoutesHelper';
import Link from '@amzn/meridian/link';
import { FeatureFlagProvider } from '@contexts/FeatureFlagContext';

import { publishKatalMetric } from 'src/metrics/publishers';
import { METHOD_NAME_PAGE_VIEW, METRIC_NAME_TOOL_OPENED, MetricType } from 'src/constants/metricConstants';
import { initialMetricsPublisher } from 'src/metrics/metrics';
import Component from './Component';

const queryClient = new QueryClient();

enum PreflightStatus {
  InProgress,
  Succeeded,
  Failed,
}

const App: FC = () => {
  /**
   * We have FederateAuth configured on backend ALB listeners: https://tiny.amazon.com/11pkdytxv/codeamazpackGSCablob34eclib
   * ALB credentials (OIDC data) are required to be present in the cookie of requests to backend.
   *
   * Problem:
   * If the frontend website sends a request to backend directly without credentials,
   * ALB listener will return a 302 response to ask frontend to fetch credentials.
   * However, because the website and backend service run on different domains
   * and the 302 response from ALB listener does not contain 'Access-Control-Allow-Origin'
   * header, the browser will refuse to follow the redirect. As a result, frontend
   * fail to communicate with backend.
   *
   * Resolution:
   * To resolve this issue, we need to retrieve ALB credentials as a preflight step
   * before loading website:
   * 1. Send a GET request in 'no-CORS' mode to backend health check route (/sping).
   * 2. ALB will return 302 without 'Access-Control-Allow-Origin' header.
   * 3. Browser will follow the 302 redirect because original request was sent in 'no-CORS' mode.
   * 4. Browser fetches ALB credentials through redirects and save them in cookie.
   * 5. Browser should be able to make regular CORS requests to backend with the ALB credentials.
   *
   * FAQ:
   * 1. Why not sending all requests in `no-CORS` mode?
   * A:
   * There are limitations for headers in 'no-CORS' request.
   * To communicate with backend (GraphQL service), `Content-Type` header needs
   * to be set to `application/json`, which is not allowed in 'no-CORS' mode.
   * Ref: https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
   *
   * 2. Can we add 'Access-Control-Allow-Origin' header in the 302 response?
   * A:
   * No. The 302 response is sent by AWS ALB listener,
   * which does not allow us to add extra custom header in it.
   *
   * 3. Why hitting `/sping` (health check) instead of the regular `/graphql` in the preflight step?
   * A:
   * The 'no-CORS' request in preflight will eventually
   * arrive at GraphQL service after retrieving credentials.
   * A GET request with empty request body is not a valid GraphQL request,
   * which may lead to unwanted metrics and cause false alarms.
   * For the purpose of fetching ALB credential, hitting `/sping` is sufficed.
   * */
  const [preflightStatus, setPreflightStatus] = useState(PreflightStatus.InProgress);
  useEffect(() => {
    // a hacky way of constructing health check url
    const backendHealthCheckUrl = ('https://api.us-west-2.alpha.categorymanagement.wwgs.amazon.dev/graphql' as string).replace('graphql', 'sping');

    const fetchAndValidateCredential = async () => {
      // Send 'no-cors' request to fetch credentials.
      // The response status code would be 0 for 'no-cors' requests,
      // which cannot indicate if the credential retrieval is successful or not.
      await fetch(backendHealthCheckUrl, {
        method: 'GET',
        credentials: 'include',
        mode: 'no-cors',
        redirect: 'follow',
      });
      // validate credentials by sending a regular CORS request to health check url.
      // If the credentials is valid, we should get a 200 response.
      // Otherwise, the request won't get response until timeout due to CORS error.
      fetch(backendHealthCheckUrl, {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        redirect: 'error',
      })
        .then((response) => {
          if (response.status === 200) {
            setPreflightStatus(PreflightStatus.Succeeded);
          } else {
            setPreflightStatus(PreflightStatus.Failed);
            void logger.error(`Preflight validation failed with response: ${JSON.stringify(response)}`);
          }
          return 'Preflight Succeed.';
        })
        .catch((error: Error) => {
          setPreflightStatus(PreflightStatus.Failed);
          void logger.error(`Preflight validation failed: ${error.name} ${error.message}`);
        });
    };
    void fetchAndValidateCredential();
    const initialActionPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(METHOD_NAME_PAGE_VIEW);
    publishKatalMetric(initialActionPublisher, METRIC_NAME_TOOL_OPENED, 1, MetricType.Counter);
  }, []);

  if (preflightStatus === PreflightStatus.InProgress) {
    return (
      <Box>
        <Loader size="large" data-cy="app_loading" />
      </Box>
    );
  }
  if (preflightStatus === PreflightStatus.Failed) {
    return (
      <Box data-cy="app_loading_failed">
        Failed to Load. Please log into&nbsp;
        <Link href={getMidwayRedirectRoutes(('alpha' as string).toLowerCase())}>Midway</Link>
        .
        <br />
        If this message kept showing, please use&nbsp;
        <Link href={TICKETING_PATH}>this link</Link> to submit a ticket.
      </Box>
    );
  }

  return (
    <FeatureFlagProvider>
      <KbarWrapper>
        <ErrorBoundary>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <NotificationBanner />
              <BrowserRouter>
                <AppContextProvider>
                  <Component />
                </AppContextProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </Provider>
        </ErrorBoundary>
      </KbarWrapper>
    </FeatureFlagProvider>
  );
};

export default App;
